// The catalog as markdown, for the AI system prompt: what record types exist
// and which fields each schema declares. Read when an agent prompt is built,
// from whatever the catalog holds at that moment.
import type { RecordType, Schema, SchemaField } from "@auger/core"

function renderFieldType(f: SchemaField): string {
  if (f.type === "select" && Array.isArray(f.values) && f.values.length) {
    return `enum[${f.values.join(", ")}]`
  }
  if (f.type === "link") {
    const target = f.recordType || "record"
    return f.isArray ? `link<${target}> (array)` : `link<${target}>`
  }
  return f.isArray ? `${f.type} (array)` : f.type
}

function renderFieldSummary(f: SchemaField): string {
  const trail: string[] = []
  if (f.description) trail.push(f.description)
  const suffix = trail.length ? ` — ${trail.join("; ")}` : ""
  return `    - ${f.key}: ${renderFieldType(f)}${suffix}`
}

function renderSchema(schema: Schema): string {
  const desc = schema.description ? ` — ${schema.description}` : ""
  const lines = [`  - **${schema.name}**${desc}`]
  const fields = Array.isArray(schema.fields) ? schema.fields : []
  for (const f of fields) lines.push(renderFieldSummary(f))
  return lines.join("\n")
}

const byName = (a: Schema, b: Schema) => a.name.localeCompare(b.name)

/** Every record type, with its description and default schema. */
export function typesToMarkdown(types: RecordType[]): string {
  const lines = ["# Record Types", ""]

  for (const type of types) {
    const label = type.label || type.name
    lines.push(`## ${label} (\`${type.name}\`)`)
    if (type.description) lines.push(type.description)
    if (type.schema?.name) {
      lines.push(`Default schema: ${type.schema.name}`)
    }
    lines.push("")
  }

  return lines.join("\n").trimEnd() + "\n"
}

/** Every schema with its fields: typed ones grouped by record type, then listing schemas. */
export function schemasToMarkdown(schemas: Schema[]): string {
  const groups: Record<string, Schema[]> = {}
  for (const s of schemas) (groups[s.record_type || "_untyped"] ??= []).push(s)
  const recordTypes = Object.keys(groups)
    .filter((k) => k !== "_untyped")
    .sort()
  const lines = [
    "# Schemas (subtypes of record types)",
    "",
    "Each record has a single `schema_id` pointing to one of these definitions — its **subtype**. A type has a default schema but individual records can override.",
    "",
  ]

  for (const recordType of recordTypes) {
    lines.push(`## \`${recordType}\` — subtypes`)
    for (const s of groups[recordType]!.slice().sort(byName)) lines.push(renderSchema(s))
    lines.push("")
  }

  const untyped = groups._untyped ?? []
  if (untyped.length) {
    lines.push(
      "## Listing schemas",
      "_These schemas describe **listing rows** (the membership relation — e.g. a person's role in an Employees list), not record rows. The row is stored in `record_listings` and its typed fields live under `record_listings.data`._"
    )
    for (const s of untyped.slice().sort(byName)) lines.push(renderSchema(s))
    lines.push("")
  }

  return lines.join("\n").trimEnd() + "\n"
}
